import { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import type { SelectOption } from "@/types";

interface SearchableSelectProps {
  value: string;
  /** Simple string options or rich options with description + confidence */
  options: string[] | SelectOption[];
  onChange: (option: SelectOption | null) => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
  /** When true, keep option order after filtering (no confidence/label sort). */
  preserveOptionsOrder?: boolean;
}

function normaliseOptions(raw: string[] | SelectOption[]): SelectOption[] {
  if (raw.length === 0) return [];
  if (typeof raw[0] === "string") {
    return (raw as string[]).map((s) => ({ value: s, label: s, confidence: undefined }));
  }
  return raw as SelectOption[];
}

/**
 * A combobox-style dropdown with inline search filtering.
 * Supports rich options with description and confidence score.
 * Renders the dropdown via a portal so it is never clipped
 * by parent overflow boundaries (e.g. the mapping table).
 */
export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = "Select column",
  hasError = false,
  className,
  preserveOptionsOrder = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const normalised = useMemo(() => normaliseOptions(options), [options]);

  const filtered = useMemo(() => {
    let result = normalised;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.description && o.description.toLowerCase().includes(q))
      );
    }
    if (preserveOptionsOrder) {
      return result;
    }
    // Sort by confidence descending (highest first), then alphabetical
    return [...result].sort((a, b) => {
      const confA = a.confidence ?? -1;
      const confB = b.confidence ?? -1;
      if (confA !== confB) return confB - confA;
      return a.label.localeCompare(b.label);
    });
  }, [normalised, search, preserveOptionsOrder]);

  const handleSelect = useCallback(
    (opt: SelectOption) => {
      onChange(opt);
      setSearch("");
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
      setSearch("");
    },
    [onChange]
  );

  // Recompute position whenever dropdown opens or window scrolls/resizes
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScrollResize = () => updatePosition();
    window.addEventListener("scroll", handleScrollResize, true);
    window.addEventListener("resize", handleScrollResize);
    return () => {
      window.removeEventListener("scroll", handleScrollResize, true);
      window.removeEventListener("resize", handleScrollResize);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearch("");
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className={cn("relative", className)}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full h-9 px-3 flex items-center justify-between gap-2 rounded-md border text-sm transition-all duration-150",
          "bg-white hover:bg-[var(--color-bg-secondary)]",
          hasError ? "border-red-300 ring-1 ring-red-200" : "border-[var(--color-border-light)]",
          isOpen &&
            "ring-2 ring-[var(--color-brand-primary)]/30 border-[var(--color-brand-primary)]"
        )}
      >
        <span
          className={cn(
            "truncate text-left",
            value ? "text-[var(--color-text-primary)]" : "text-[var(--color-text-tertiary)]"
          )}
        >
          {value || placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              onClick={handleClear}
              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] cursor-pointer"
            >
              x
            </span>
          )}
          <svg
            className={cn(
              "w-3.5 h-3.5 text-[var(--color-text-tertiary)] transition-transform",
              isOpen && "rotate-180"
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {/* Dropdown -- rendered via portal to escape overflow clipping */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="fixed z-[9999] bg-white rounded-lg border border-[var(--color-border-light)] shadow-lg overflow-hidden"
              style={{
                top: pos.top,
                left: pos.left,
                width: pos.width,
              }}
            >
              {/* Search input */}
              <div className="p-2 border-b border-[var(--color-border-light)]">
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full h-8 px-2.5 text-sm rounded border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)]/30 focus:border-[var(--color-brand-primary)] placeholder:text-[var(--color-text-tertiary)]"
                />
              </div>

              {/* Options list */}
              <div className="max-h-60 overflow-y-auto overscroll-contain">
                {filtered.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-[var(--color-text-tertiary)]">
                    No matching fields
                  </div>
                ) : (
                  filtered.map((opt) => {
                    const isSelected = opt.value === value || opt.label === value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleSelect(opt)}
                        className={cn(
                          "w-full text-left px-3 py-2 transition-colors hover:bg-[var(--color-bg-secondary)] border-b border-[var(--color-border-light)]/50 last:border-b-0",
                          isSelected && "bg-[var(--color-brand-surface)]"
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={cn(
                              "text-sm truncate",
                              isSelected
                                ? "font-medium text-[var(--color-brand-primary-dark)]"
                                : "text-[var(--color-text-primary)]"
                            )}
                          >
                            {opt.label}
                          </span>
                          {opt.confidence !== undefined && opt.confidence > 0 && (
                            <span
                              className={cn(
                                "text-[10px] font-mono tabular-nums shrink-0 px-1.5 py-0.5 rounded-full",
                                opt.confidence >= 0.85
                                  ? "bg-emerald-50 text-emerald-600"
                                  : opt.confidence >= 0.7
                                    ? "bg-amber-50 text-amber-600"
                                    : "bg-red-50 text-red-600"
                              )}
                            >
                              {Math.round(opt.confidence * 100)}%
                            </span>
                          )}
                        </div>
                        {opt.description && (
                          <p className="text-[10px] text-[var(--color-text-tertiary)] mt-0.5 leading-snug line-clamp-2">
                            {opt.description}
                          </p>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
